import React from 'react';
import { mathFormat, toTwoDecimalNumber } from '@sg/utils/common';
import { localizeLabel } from '@sg/utils/translate';
import DefinitionBubble from '@/pages/strategic/components/DefinitionBubble';
import DataProcessing from '@sg/components/DataProcessing';
import TableContraction from '@sg/assests/images/Table-Contraction.svg';
import TableExpands from '@sg/assests/images/Table-Expands.svg';
import DeafaultGame from '../assests/DeafaultGame.png';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
import PhaseCell from '@sg/components/PhaseCell';
import ExpandCollapseButton from '@sg/components/ExpandCollapseButton';
import HeadcountEditableCell from './components/HeadcountEditableCell';
import { createBtnPoint, SGBuriedBtnPoint, PublishingHeadcountTab } from '@/pages/strategic/utils/buriedPoint';

const width = 260;

interface BuildHeadcountColumnsParams {
  subject_totals: any[];
  expandedTeamsMap: Record<string, any[]>;
  setExpandedTeamsMap: (value: React.SetStateAction<Record<string, any[]>>) => void;
  formatMessage: any;
  projectColumnRender: (...args: any[]) => React.ReactNode;
  suggestedHCColumnRender?: (...args: any[]) => React.ReactNode;
  suggestedValueColumnRender?: (...args: any[]) => React.ReactNode;
  expandCollapseOptions?: { isAllExpanded?: boolean; onToggleExpandAll?: () => void };
  showModelSuggestedHC?: boolean;
}

const defaultSubjectCellRender = (cvm: any, subjectName: string) => {
  // console.log('cvm', cvm);
  
  return (
    <div style={{ textAlign: 'right' }}>
    <DataProcessing
      content={['', 'MoM']}
      data={cvm?.[subjectName]}
      dataSize={14}
      isChangeContentPosition={true}
      rate={cvm?.[`${subjectName}MoM`]}
      useRawValue={true}
      valueFormatter={toTwoDecimalNumber}
    />
  </div>
  )
};

const defaultTotalCellRender = (cvm: any) => (
  <div style={{textAlign: 'right'}}>
    <DataProcessing
      content={['', 'MoM']}
      data={cvm?.['Total']}
      dataSize={14}
      isChangeContentPosition={true}
      rate={ cvm?.['TotalMoM']}
      valueFormatter={toTwoDecimalNumber}
    />
  </div>
);

/**
 * Get Head Rate Table Columns
 * Used for the Projects Headcount chart table view
 */
export const getHeadRateColumns = (formatMessage: any) => [
  {
    header: formatMessage({ id: 'dbs_date' }),
    dataIndex: 'date',
    key: 'date',
    width: 100,
  },
  {
    header: <div style={{ textAlign: 'right', lineHeight: '20px'}}>{formatMessage({ id: 'dbs_publishing_hc' })}</div>,
    dataIndex: 'Publishing HC',
    key: 'Publishing HC',
    width: 160,
    format: mathFormat,
    render: (item: any)=> <div style={{textAlign: 'right', textWrap: 'nowrap'}}>{mathFormat(item?.['Publishing HC']) || '-'}</div>
  },
  {
    header:  <div style={{ textAlign: 'right', lineHeight: '20px'}}>{formatMessage({ id: 'dbs_capability_box' })}</div>,
    dataIndex: 'Capability Box',
    key: 'Capability Box',
    width: 160,
    format: mathFormat,
    render: (item: any)=> <div style={{textAlign: 'right', textWrap: 'nowrap'}}>{mathFormat(item?.['Capability Box']) || '-'}</div>
  },
  {
    header:  <div style={{ textAlign: 'right', lineHeight: '20px'}}>{formatMessage({ id: 'dbs_subtitle_studios' })}</div>,
    dataIndex: 'Studios',
    key: 'Studios',
    width: 160,
    format: mathFormat,
    render: (item: any)=> <div style={{textAlign: 'right', textWrap: 'nowrap'}}>{mathFormat(item?.Studios) || '-'}</div>
  },
  {
    header:  <div style={{ textAlign: 'right', lineHeight: '20px' }}>{formatMessage({ id: 'dbs_charts_legend_kpi' })}</div>,
    dataIndex: 'KPI',
    key: 'KPI',
    width: 100,
    format: mathFormat,
    render: (item: any)=> <div style={{textAlign: 'right', textWrap: 'nowrap'}}>{mathFormat(item?.KPI) || '-'}</div>
  },
];

/**
 * Get Subtotal Columns for Publishing Business
 */
export const getPublishingSubtotalColumns = (
  formatMessage: any,
  setSubtotalColoums: any
) => [
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>PUBGM Publishing</div>,
    dataIndex: 'PUBGM Publishing',
    key: 'PUBGM Publishing',
    width: 140,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['PUBGM Publishing'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Esports</div>,
    dataIndex: 'Esports',
    key: 'Esports',
    width: 140,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Esports'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Nebula Collaboration Team</div>,
    dataIndex: 'Nebula Collaboration Team',
    key: 'Nebula Collaboration Team',
    width: 180,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Nebula Collaboration Team'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Regional Publishing</div>,
    dataIndex: 'Regional Publishing',
    key: 'Regional Publishing',
    width: 160,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Regional Publishing'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Neptune Publishing</div>,
    dataIndex: 'Neptune Publishing',
    key: 'Neptune Publishing',
    width: 120,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Neptune Publishing'] || '-'
  },
  {
    header: ()=> {
      return <div className="dbs-table-expands-and-contract">
        <div style={{lineHeight: '20px', width: '80px', display: 'inline-block'}}>Sirius Publishing</div>
        <img src={TableExpands} alt="" className="dbs-table-expands-and-contract-img" onClick={()=> {
          setSubtotalColoums([])
        }} />
      </div>
    },
    dataIndex: 'Sirius Publishing',
    key: 'Sirius Publishing',
    width: 120,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Sirius Publishing']
  },
];

/**
 * Get Subtotal Columns for Capability Box
 */
export const getCapabilityBoxSubtotalColumns = (
  formatMessage: any,
  setBoxSubtotalColoums: any
) => [
  {
    header: <div className="dbs-table-expands-and-contract"><div style={{textAlign: 'right', lineHeight: '20px'}}>Tech</div></div>,
    dataIndex: 'Tech',
    key: 'Tech',
    width: 190,
    showHiddenColor: true,
    render: (cvm: any)=><div style={{textAlign: 'right'}}>
      <DataProcessing content={['', 'MoM']} data={cvm?.['Tech']} dataSize={14} isChangeContentPosition={true} rate={ cvm?.['TechMoM'] || '-'} />
    </div>
  },
  {
    header: ()=> {
      return <div className="dbs-table-expands-and-contract">
        <div style={{textAlign: 'right', lineHeight: '20px'}}>Others</div>
        <img src={TableExpands} alt="" style={{top: 0}} className="dbs-table-expands-and-contract-img" onClick={()=> {
          setBoxSubtotalColoums([])
        }} />
      </div>
    },
    dataIndex: 'Others',
    key: 'Others',
    width: 190,
    showHiddenColor: true,
    render: (cvm: any)=> <div style={{textAlign: 'right'}}>
      <DataProcessing content={['', 'MoM']} data={cvm?.['Others']} dataSize={14} isChangeContentPosition={true} rate={ cvm?.['OthersMoM'] || '-' } />
    </div>
  },
];

/**
 * Get Subtotal Columns for Studios
 */
export const getStudiosSubtotalColumns = (
  formatMessage: any,
  setStudiosSubtotalColoums: any
) => [
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Amplifier Partner Team</div>,
    dataIndex: 'Amplifier Partner Team',
    key: 'Amplifier Partner Team',
    width: 140,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Amplifier Partner Team'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Astra Partner Team</div>,
    dataIndex: 'Astra Partner Team',
    key: 'Astra Partner Team',
    width: 140,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Astra Partner Team'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Nexus Partner Team</div>,
    dataIndex: 'Nexus Partner Team',
    key: 'Nexus Partner Team',
    width: 140,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Nexus Partner Team'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Saturn Partner Team</div>,
    dataIndex: 'Saturn Partner Team',
    key: 'Saturn Partner Team',
    width: 140,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Saturn Partner Team'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Fuel Partner Team</div>,
    dataIndex: 'Fuel Partner Team',
    key: 'Fuel Partner Team',
    width: 140,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Fuel Partner Team'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Nova Collaboration Team</div>,
    dataIndex: 'Nova Collaboration Team',
    key: 'Nova Collaboration Team',
    width: 190,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Nova Collaboration Team'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Solar Partner Team</div>,
    dataIndex: 'Solar Partner Team',
    key: 'Solar Partner Team',
    width: 140,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Solar Partner Team'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Dev & Portfolio Support</div>,
    dataIndex: 'Dev & Portfolio Support',
    key: 'Dev & Portfolio Support',
    width: 160,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Dev & Portfolio Support'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Copernicus Collaboration Team</div>,
    dataIndex: 'Copernicus Collaboration Team',
    key: 'Copernicus Collaboration Team',
    width: 180,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Copernicus Collaboration Team'] || '-'
  },
  {
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>Venture Lab</div>,
    dataIndex: 'Venture Lab',
    key: 'Venture Lab',
    width: 120,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Venture Lab'] || '-'
  },
  {
    header: ()=> {
      return <div className="dbs-table-expands-and-contract">
        <div style={{textAlign: 'right', lineHeight: '20px'}}>Next Studios</div>
        <img src={TableExpands} alt="" className="dbs-table-expands-and-contract-img" onClick={()=> {
          setStudiosSubtotalColoums([])
        }} />
      </div>
    },
    dataIndex: 'Next Studios',
    key: 'Next Studios',
    width: 120,
    showHiddenColor: true,
    render: (cvm: any)=> cvm?.['Next Studios'] || '-'
  },
];

/**
 * Get Default Headcount Projects Table Columns
 * This is the main table configuration for Projects Headcount table
 */
export const getDefaultHeadcountColumns = (
  formatMessage: any,
  subtotalColoums: any[],
  boxSubtotalColoums: any[],
  studiosSubtotalColoums: any[],
  setSubtotalColoums: any,
  setBoxSubtotalColoums: any,
  setStudiosSubtotalColoums: any
) => [
  {
    header: <div style={{marginLeft: '3px'}}>{formatMessage({ id: 'dbs_project' })}</div>,
    dataIndex: 'Project',
    key: 'Project',
    fixed: "left",
    render: (cvm: any)=> <div style={{display: 'inline-flex', alignItems: 'center'}}>
        {!cvm.children && cvm?.Project !== 'Total' && <img src={cvm?.cover || DeafaultGame} alt="" className="dbs-project-icon" style={{marginLeft: '6px'}} />}
        <span style={{fontWeight: 700, marginLeft: cvm.children ? '-11px' : (cvm?.Project !== 'Total' ? '11px' : '5px')}}> {localizeLabel(cvm?.Project, formatMessage)}</span>
    </div>
  },
  {
    header: (
      <DefinitionBubble 
        definition={formatMessage({id: 'dbs_model_suggested_hc_definition'})}
        updateTime={formatMessage({id: 'dbs_update_time_month'})}
        source={formatMessage({id: 'dbs_update_source_pmi'})}
      >
        <div style={{textAlign: 'right', lineHeight: '20px'}}>Model's Suggested HC</div>
      </DefinitionBubble>
    ),
    dataIndex: `Model's Suggested HC`,
    key: `Model's Suggested HC`,
    width,
    render: (cvm: any)=> {
      const value = cvm?.[`Model's Suggested HC`];
      // If value is a number (from status total), format it; if string, display directly; otherwise show '-'
      if (value === null || value === undefined || value === '') {
        return <div style={{textAlign: 'right', textWrap: 'nowrap'}}>-</div>;
      }
      if (typeof value === 'number') {
        return <div style={{textAlign: 'right', textWrap: 'nowrap'}}>{toTwoDecimalNumber(value)}</div>;
      }
      return <div style={{textAlign: 'right', textWrap: 'nowrap'}}>{value}</div>;
    }
  },
  {
    header: (
      <DefinitionBubble 
        definition={formatMessage({id: 'dbs_suggested_value_definition'})}
        updateTime={formatMessage({id: 'dbs_update_time_month'})}
        source={formatMessage({id: 'dbs_update_source_pmi'})}
      >
        <div style={{textAlign: 'right', lineHeight: '20px'}}>Suggested Value</div>
      </DefinitionBubble>
    ),
    dataIndex: 'Suggested Value',
    key: 'Suggested Value',
    width,
    render: (cvm: any)=> {
      const value = cvm?.['Suggested Value'];
      // If value is a number (from status total), format it; if string, display directly; otherwise show '-'
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      if (typeof value === 'number') {
        return toTwoDecimalNumber(value);
      }
      return value;
    }
  },
  {
    header: ()=> {
      return <div className="dbs-table-expands-and-contract">
        <div style={{lineHeight: '20px',width: '125px', display: 'inline-block'}}>{formatMessage({ id: 'dbs_publishing_business_subtotal' })}</div>
        {subtotalColoums.length === 0 && <img src={TableContraction} alt="" className="dbs-table-expands-and-contract-img" onClick={()=> {
          setSubtotalColoums(getPublishingSubtotalColumns(formatMessage, setSubtotalColoums));
        }}/>}
      </div>
    },
    dataIndex: 'Publishing Buisness Subtotal',
    key: 'Publishing Buisness Subtotal',
    width,
    render: (cvm: any)=> {
      return <div style={{textAlign: 'right'}}>
      <DataProcessing content={['', 'MoM']} data={cvm?.['Publishing Buisness Subtotal']} dataSize={14} isChangeContentPosition={true} rate={ cvm?.BSMoM || '-' } />
      </div>
    }
  },
  ...subtotalColoums,
  {
    header: ()=> {
      return <div className="dbs-table-expands-and-contract">
        <div style={{lineHeight: '20px',width: '100px', display: 'inline-block'}}>{formatMessage({ id: 'dbs_capability_box_subtotal' })}</div>
        {boxSubtotalColoums.length === 0 && <img src={TableContraction} alt="" className="dbs-table-expands-and-contract-img" onClick={()=> {
          setBoxSubtotalColoums(getCapabilityBoxSubtotalColumns(formatMessage, setBoxSubtotalColoums));
        }}/>}
      </div>
    },
    dataIndex: 'Capability Box Subtotal',
    key: 'Capability Box Subtotal',
    width,
    render: (cvm: any)=><div style={{textAlign: 'right'}}>
      <DataProcessing content={['', 'MoM']} data={cvm?.['Capability Box Subtotal']} dataSize={14} isChangeContentPosition={true} rate={ cvm?.CBSMoM || '-' } />
      </div>
  },
  ...boxSubtotalColoums,
  {
    header: ()=> {
      return <div className="dbs-table-expands-and-contract">
        <div style={{lineHeight: '20px',width: '100px', display: 'inline-block'}}>{formatMessage({ id: 'dbs_studios_subtotal' })}</div>
        {studiosSubtotalColoums.length === 0 && <img src={TableContraction} alt="" className="dbs-table-expands-and-contract-img" onClick={()=> {
          setStudiosSubtotalColoums(getStudiosSubtotalColumns(formatMessage, setStudiosSubtotalColoums));
        }}/>}
      </div>
    },
    dataIndex: 'Studios Subtotal',
    key: 'Studios Subtotal',
    width,
    render: (cvm: any)=> cvm?.['Studios Subtotal'] || '-'
  },
  ...studiosSubtotalColoums,
  {
    header: <div className="dbs-table-expands-and-contract"><div style={{textAlign: 'right', lineHeight: '20px'}}>Total</div></div>,
    dataIndex: 'Total',
    key: 'Total',
    width: 190,
    render: (cvm: any)=><div style={{textAlign: 'right'}}>
      <DataProcessing content={['', 'MoM']} data={cvm?.['Total']} dataSize={14} isChangeContentPosition={true} rate={ cvm?.['TotalMoM'] || '-' } />
    </div>
  },
];

/**
 * Generate Teams Columns for a subject
 * Helper function to create columns for each team in a subject
 */
const generateTeamsColumns = (subject: any, formatMessage: any) => {
  return subject.teams.map((team: any) => ({
    header: <div style={{textAlign: 'right', lineHeight: '20px'}}>{localizeLabel(team.team, formatMessage)}</div>,
    dataIndex: team.team,
    key: team.team,
    width: 200,
    showHiddenColor: true,
    render: (cvm: any) => {
      const value = cvm?.[team.team];
      if (value === null || value === undefined) {
        return <div style={{textAlign: 'right'}}>-</div>;
      }
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return <div style={{textAlign: 'right'}}>-</div>;
      }
      return <div style={{textAlign: 'right'}}>{numeric.toFixed(2)}</div>;
    }
  }));
};

/**
 * Handle click on expand/collapse icon
 * Toggle teams columns after the subject column
 */
export const handleToggleTeams = (
  subject: any,
  expandedTeamsMap: Record<string, any[]>,
  setExpandedTeamsMap: (value: React.SetStateAction<Record<string, any[]>>) => void,
  formatMessage: any
) => {
  const isExpanded = expandedTeamsMap[subject.subject]?.length > 0;
  
  if (isExpanded) {
    // 收起：清空该 subject 的 teams
    setExpandedTeamsMap(prev => ({
      ...prev,
      [subject.subject]: []
    }));
  } else {
    // 展开：生成该 subject 的 teams 列
    setExpandedTeamsMap(prev => ({
      ...prev,
      [subject.subject]: generateTeamsColumns(subject, formatMessage)
    }));
  }
};

const buildHeadcountColumns = ({
  subject_totals,
  expandedTeamsMap,
  setExpandedTeamsMap,
  formatMessage,
  projectColumnRender,
  suggestedHCColumnRender,
  suggestedValueColumnRender,
  expandCollapseOptions,
  showModelSuggestedHC = true,
}: BuildHeadcountColumnsParams): any[] => {
  const teamsMap = expandedTeamsMap || {};
  const setTeamsMap = typeof setExpandedTeamsMap === 'function' ? setExpandedTeamsMap : () => {};

  const columns: any[] = [
    {
      header: (
          <div>
              {expandCollapseOptions?.onToggleExpandAll ? (
                  <ExpandCollapseButton
                      isExpanded={!!expandCollapseOptions.isAllExpanded}
                      onClick={() => expandCollapseOptions.onToggleExpandAll!()}
                      onTrackToggle={(nextExpanded) => {
                        const btnPoint = createBtnPoint(
                          PublishingHeadcountTab,
                          'projectHeadcountTableCollapse',
                          nextExpanded
                            ? 'Project Headcount Table Expand All'
                            : 'Project Headcount Table Collapse All',
                        );
                        if (btnPoint) {
                          SGBuriedBtnPoint(btnPoint);
                        }
                      }}
                      title={formatMessage({ id: 'dbs_project' })}
                  />
              ) : (
                  <span style={{ fontWeight: 700 }}>
                      {formatMessage({ id: 'dbs_project' })}
                  </span>
              )}
          </div>
      ),
      dataIndex: 'Project',
      key: 'Project',
      width: 300,
      fixed: "left",
      align: 'left', // Explicitly set alignment to prevent CSS from shifting it
      render: projectColumnRender,
    },
  ];

  // Conditionally add "Model's Suggested HC" column
  if (showModelSuggestedHC) {
    columns.push({
      header: (
        <DefinitionBubble 
          definition={formatMessage({id: 'dbs_model_suggested_hc_definition'})}
          updateTime={formatMessage({id: 'dbs_update_time_month'})}
          source={formatMessage({id: 'dbs_update_source_pmi'})}
        >
          <div style={{textAlign: 'right', lineHeight: '20px'}}>Model's Suggested HC</div>
        </DefinitionBubble>
      ),
      dataIndex: `Model's Suggested HC`,
      key: `Model's Suggested HC`,
      width,
      render: suggestedHCColumnRender || ((cvm: any)=> {
        const value = cvm?.[`Model's Suggested HC`];
        // If value is a string (from str_headcount), display it directly; otherwise format as number
        return <div style={{textAlign: 'right', textWrap: 'nowrap'}}>
          {typeof value === 'string' ? value : toTwoDecimalNumber(value)}
        </div>
      })
    });
  }

  // Always add "Suggested Value" column
  columns.push({
    header: (
      <DefinitionBubble 
        definition={formatMessage({id: 'dbs_suggested_value_definition'})}
        updateTime={formatMessage({id: 'dbs_update_time_month'})}
        source={formatMessage({id: 'dbs_update_source_pmi'})}
      >
        <div style={{textAlign: 'right', lineHeight: '20px'}}>Suggested Value</div>
      </DefinitionBubble>
    ),
    dataIndex: 'Suggested Value',
    key: 'Suggested Value',
    width,
    render: suggestedValueColumnRender || ((cvm: any)=> {
      const value = cvm?.['Suggested Value'];
      // If value is a string (from str_headcount), display it directly; otherwise format as number
      // return typeof value === 'string' ? value : toTwoDecimalNumber(value);

      // Handle display formatting according to requirements
      if (value === null || value === undefined || value === '') {
        return '0.0';
      }
      
      const strValue = String(value).trim().toLowerCase();
      if (strValue === 'na' || strValue === 'n/a') {
        return 'NA';
      }
      
      // Try to parse as number and format to 1 decimal
      const numVal = parseFloat(String(value));
      if (!isNaN(numVal)) {
        return numVal.toFixed(1);
      }
      
      // Return as-is for other text
      return String(value);
    })
  });

  subject_totals.forEach((subject: any) => {
    const subjectName = subject.subject;
    const isExpanded = (teamsMap[subjectName]?.length ?? 0) > 0;
    const hasTeams = Array.isArray(subject.teams) && subject.teams.length > 0;

    columns.push({
      dataIndex: subjectName,
      key: subjectName,
      width,
      header: () => (
        <div className="dbs-table-expands-and-contract" style={{ overflow: 'visible' }}>
          <div style={{ lineHeight: '20px', display: 'inline-block' }}>
            {localizeLabel(subjectName, formatMessage)}
          </div>
          {hasTeams && (
            <img
              src={isExpanded ? TableExpands : TableContraction}
              alt=""
              className="dbs-table-expands-and-contract-img"
              onClick={() => setTeamsMap && handleToggleTeams(subject, teamsMap, setTeamsMap, formatMessage)}
            />
          )}
        </div>
      ),
      render: (cvm: any) => defaultSubjectCellRender(cvm, subjectName)
    });

    if (isExpanded && teamsMap[subjectName]?.length) {
      columns.push(...teamsMap[subjectName]);
    }
  });

  columns.push({
    header: <div className="dbs-table-expands-and-contract"><div style={{textAlign: 'right', lineHeight: '20px'}}>Total</div></div>,
    dataIndex: 'Total',
    key: 'Total',
    width: 190,
    render: (cvm: any) => defaultTotalCellRender(cvm)
  });

  return columns;
};

export const generateProjectHeadcountColumns = (
  subject_totals: any[],
  expandedTeamsMap: Record<string, any[]>,
  setExpandedTeamsMap: (value: React.SetStateAction<Record<string, any[]>>) => void,
  formatMessage: any,
  expandCollapseOptions?: { isAllExpanded?: boolean; onToggleExpandAll?: () => void },
  showModelSuggestedHC: boolean = true
): any[] =>
  buildHeadcountColumns({
    subject_totals,
    expandedTeamsMap,
    setExpandedTeamsMap,
    formatMessage,
    expandCollapseOptions,
    showModelSuggestedHC,
    projectColumnRender: (cvm: any) => (
      <div style={{display: 'inline-flex', alignItems: 'center'}}>
        {!cvm.children && cvm?.Project !== 'Total' && <img src={cvm?.cover || DeafaultGame} alt="" className="dbs-project-icon" style={{marginLeft: '22px'}} />}
        <span style={{fontWeight: 700, marginLeft: cvm.children ? '' : (cvm?.Project !== 'Total' ? '10px' : '22px')}}> {localizeLabel(cvm?.Project, formatMessage)}</span>
      </div>
    ),
  });

export const generateProjectHeadcountData = (data: any): any[] => {
  const { subject_totals, statuses } = data || {};
  if (!subject_totals?.length || !statuses?.length) {
    return [];
  }
  const projectHeadcountData: any[] = [];

  // 处理status列
  const statusRows: any[] = [];
  statuses.forEach((status: any) => {
    const statusRow: any = {
      Project: status.status,
      id: `status-${status.status}`,
      children: [],
      Total: 0,
      isCategory: true, // 明确标识为一级目录（status）
    };

    const statusSums: Record<string, number> = {};
    // Track if special columns have any numeric values
    const specialColumnHasNumeric: Record<string, boolean> = {};
    // Track range values (first-second*) for special columns
    const specialColumnRangeSums: Record<string, { first: number; second: number; hasRange: boolean }> = {};

    status.projects?.forEach((project: any) => {
      const projectRow: any = {
        Project: project.project_name,
        id: project.project_id,
        cover: project.cover,
        isCategory: false, // 明确标识为二级项目
      };

      const projectTotal = Number(project.total);
      projectRow.Total = Number.isFinite(projectTotal) ? projectTotal : 0;
      statusRow.Total += projectRow.Total;

      project.subjects?.forEach((subjectItem: any) => {
        const subjectName = subjectItem.subject;
        // Special handling: Use str_headcount for "Model's Suggested HC" and "Suggested Value"
        const isSpecialColumn = subjectName === "Model's Suggested HC" || subjectName === "Suggested Value";
        const subjectValue = isSpecialColumn 
          ? (subjectItem.str_headcount || '')  // Use str_headcount for special columns
          : (Number(subjectItem.headcount) || 0);  // Use headcount for other columns
        projectRow[subjectName] = subjectValue;

        const momValue = Number(subjectItem.mom);
        if (Number.isFinite(momValue)) {
          projectRow[`${subjectName}MoM`] = Number(momValue.toFixed(2));
        }

        // For special columns, try to convert string to number for summation
        if (isSpecialColumn) {
          // Initialize tracking objects if they don't exist
          if (!statusSums.hasOwnProperty(subjectName)) {
            statusSums[subjectName] = 0;
            specialColumnHasNumeric[subjectName] = false;
          }
          if (!specialColumnRangeSums.hasOwnProperty(subjectName)) {
            specialColumnRangeSums[subjectName] = { first: 0, second: 0, hasRange: false };
          }
          
          // Check if the value matches the range pattern "number-number*" (e.g., "5-10*", "1-3*")
          if (typeof subjectValue === 'string' && subjectValue.trim() !== '') {
            const rangeMatch = subjectValue.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\*$/);
            if (rangeMatch) {
              // Extract first and second values from range
              const firstValue = parseFloat(rangeMatch[1]);
              const secondValue = parseFloat(rangeMatch[2]);
              if (!isNaN(firstValue) && !isNaN(secondValue) && isFinite(firstValue) && isFinite(secondValue)) {
                // Ensure the range sums object exists before adding
                if (!specialColumnRangeSums[subjectName]) {
                  specialColumnRangeSums[subjectName] = { first: 0, second: 0, hasRange: false };
                }
                specialColumnRangeSums[subjectName].first += firstValue;
                specialColumnRangeSums[subjectName].second += secondValue;
                specialColumnRangeSums[subjectName].hasRange = true;
                specialColumnHasNumeric[subjectName] = true;
              }
            } else {
              // Try to parse as regular number
              const numericValue = parseFloat(subjectValue);
              // Only add to sum if it's a valid number (not NaN)
              if (!isNaN(numericValue) && isFinite(numericValue)) {
                statusSums[subjectName] += numericValue;
                specialColumnHasNumeric[subjectName] = true;
              }
            }
          } else {
            // If it's already a number
            const numericValue = Number(subjectValue);
            if (!isNaN(numericValue) && isFinite(numericValue)) {
              statusSums[subjectName] += numericValue;
              specialColumnHasNumeric[subjectName] = true;
            }
          }
        } else {
          // For regular columns, sum numeric values
          if (!statusSums[subjectName]) {
            statusSums[subjectName] = 0;
          }
          statusSums[subjectName] += subjectValue;
        }

        subjectItem.teams?.forEach((team: any) => {
          const teamName = team.team;
          const teamHeadcount = Number(team.headcount) || 0;
          projectRow[teamName] = teamHeadcount;

          if (!statusSums[teamName]) {
            statusSums[teamName] = 0;
          }
          statusSums[teamName] += teamHeadcount;
        });

      });

      statusRow.children.push(projectRow);
    });

    // First, handle special columns with range sums (prioritize these)
    Object.keys(specialColumnRangeSums).forEach((key) => {
      if (specialColumnRangeSums[key]?.hasRange) {
        const { first, second } = specialColumnRangeSums[key];
        // Format as "firstSum-secondSum" (e.g., "10-20")
        statusRow[key] = `${first}-${second}*`;
      }
    });
    
    // Then handle other statusSums
    Object.keys(statusSums).forEach((key) => {
      // For special columns, if no numeric values were found, set to null to display as '-'
      const isSpecialColumn = key === "Model's Suggested HC" || key === "Suggested Value";
      if (isSpecialColumn) {
        // Skip if we already set it from range sums
        if (statusRow.hasOwnProperty(key)) {
          return; // Already handled by range sums
        }
        if (!specialColumnHasNumeric[key]) {
          statusRow[key] = null; // Will be displayed as '-' in the renderer
        } else {
          // Regular numeric sum (only if no range was found)
          statusRow[key] = statusSums[key];
        }
      } else {
        statusRow[key] = statusSums[key];
      }
    });

    statusRows.push(statusRow);
  });

  // 处理total列 - calculate after all statuses are processed
  const totalRow: any = {
    Project: 'Total',
    id: 'total',
    Total: 0,
    isCategory: false, // Total 行不是目录
  };
  
  // Track totals for special columns
  const totalRangeSums: Record<string, { first: number; second: number; hasRange: boolean }> = {};
  const totalNumericSums: Record<string, number> = {};
  const totalHasNumeric: Record<string, boolean> = {};
  
  // First, process regular columns from subject_totals
  subject_totals.forEach((subject: any) => {
    const subjectName = subject.subject;
    // Skip special columns - we'll handle them separately
    if (subjectName !== "Model's Suggested HC" && subjectName !== "Suggested Value") {
      totalRow[subjectName] = subject.total ?? 0;
      totalRow.Total += subject.total ?? 0;

      subject.teams?.forEach((team: any) => {
        totalRow[team.team] = team.headcount ?? 0;
      });
    }
  });
  
  // Process special columns from status rows
  const specialColumns = ["Model's Suggested HC", "Suggested Value"];
  specialColumns.forEach((columnName) => {
    let hasAnyRange = false;
    let rangeFirstTotal = 0;
    let rangeSecondTotal = 0;
    let numericTotal = 0;
    let hasNumeric = false;
    
    statusRows.forEach((statusRow) => {
      const value = statusRow[columnName];
      
      if (value === null || value === undefined || value === '') {
        return; // Skip empty values
      }
      
      // Check if it's a range string (e.g., "10-20*")
      if (typeof value === 'string') {
        const rangeMatch = value.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\*$/);
        if (rangeMatch) {
          hasAnyRange = true;
          const firstValue = parseFloat(rangeMatch[1]);
          const secondValue = parseFloat(rangeMatch[2]);
          if (!isNaN(firstValue) && !isNaN(secondValue) && isFinite(firstValue) && isFinite(secondValue)) {
            rangeFirstTotal += firstValue;
            rangeSecondTotal += secondValue;
          }
        } else {
          // Try to parse as regular number
          const numericValue = parseFloat(value);
          if (!isNaN(numericValue) && isFinite(numericValue)) {
            numericTotal += numericValue;
            hasNumeric = true;
          }
        }
      } else if (typeof value === 'number') {
        // It's already a number
        numericTotal += value;
        hasNumeric = true;
      }
    });
    
    // If we have any range values, use range format and ignore numeric values
    if (hasAnyRange) {
      totalRow[columnName] = `${rangeFirstTotal}-${rangeSecondTotal}*`;
    } else if (hasNumeric) {
      // Only numeric values, sum them normally
      totalRow[columnName] = numericTotal;
    } else {
      // No valid values
      totalRow[columnName] = null; // Will display as '-'
    }
  });
  
  // Add total row at the beginning
  projectHeadcountData.push(totalRow);
  
  // Add all status rows
  statusRows.forEach((statusRow) => {
    projectHeadcountData.push(statusRow);
  });
  
  return projectHeadcountData;
};

/**
 * Generate Edit Columns for Project Headcount
 * Used when table is in edit mode
 */
export const generateEditProjectHeadcountColumns = (
  subject_totals: any[],
  expandedTeamsMap: Record<string, any[]>,
  setExpandedTeamsMap: (value: React.SetStateAction<Record<string, any[]>>) => void,
  validationErrors: any = {},
  formatMessage: any,
  showModelSuggestedHC: boolean = true
) => 
  buildHeadcountColumns({
    subject_totals,
    expandedTeamsMap,
    setExpandedTeamsMap,
    formatMessage,
    showModelSuggestedHC,
    projectColumnRender: (
      cvm: any,
      rowKey: string,
      recordIndex: number,
      column: any,
      columnIndex: number,
      editData: any,
      stableUpdater: (value: any)=> void,
      setEditData: any,
      validationErrorsMap: Record<string, string[]>
    ) => {
      const onChange = (value: any) => {
        stableUpdater(value);
      };

      const onDelete = () => {
        stableUpdater({ __delete: true });
      };

      const hasChildren = cvm.children && Array.isArray(cvm.children) && cvm.children.length > 0;
      const showDeleteConfig = hasChildren 
        ? { disabled: true, tooltip: formatMessage({ id: 'dbs_cannot_delete_category_while_contains_projects' }) }
        : { disabled: false };

      const isNewRow = cvm.id && (String(cvm.id).startsWith('new_project_') || String(cvm.id).startsWith('new_category_'));
      const recordId = String(cvm.id);
      const recordErrors = (validationErrorsMap || validationErrors || {})[recordId] || [];
      const hasError = recordErrors.includes('Project');

      return (
        <div  style={{display: 'inline-flex', alignItems: 'center', marginLeft: '-5px'}}>
          {!cvm.children && cvm?.Project !== 'Total' && <img src={cvm?.cover || GameDefault} alt="" className="dbs-project-icon" style={{marginLeft: '6px'}} />}
          <span style={{fontWeight: 700, marginLeft: '6px'}}>
            {cvm.children ? (
              <PhaseCell 
                titleText={localizeLabel(cvm?.Project, formatMessage)} 
                onChange={onChange}
                onDelete={onDelete}
                showDelete={showDeleteConfig}
                formatMessage={formatMessage}
                isNewRow={isNewRow}
                hasError={hasError}
                deleteMessage={formatMessage({ id: 'dbs_delete_category_confirm' })}
              />
            ) : (
              <span>{localizeLabel(cvm?.Project, formatMessage)}</span>
            )}
          </span>
        </div>
      );
    },
    suggestedHCColumnRender: (
      cvm: any,
      rowKey: string,
      recordIndex: number,
      column: any,
      columnIndex: number,
      editData: any,
      stableUpdater: (value: any) => void
    ) => {
      // Only editable for project rows (not categories, not Total row)
      const isProject = cvm.isCategory === false && cvm?.Project !== 'Total';

      if (isProject) {
        // Editable input for project rows
        const onChange = (value: string) => {
          // Model's Suggested HC: accept any text including range format (e.g., "3-5*")
          if (value === '') {
            stableUpdater(null);
          } else {
            stableUpdater(value);
          }
        };

        const currentValue = cvm?.[`Model's Suggested HC`];

        return (
          <HeadcountEditableCell
            initialValue={currentValue}
            onChange={onChange}
            type="suggestedHC"
          />
        );
      } else {
        // Display-only for category and Total rows
        const value = cvm?.[`Model's Suggested HC`];
        return <div style={{textAlign: 'right', textWrap: 'nowrap'}}>
          {value === null || value === undefined || value === '' ? '-' : String(value)}
        </div>
      }
    },
    suggestedValueColumnRender: (
      cvm: any,
      rowKey: string,
      recordIndex: number,
      column: any,
      columnIndex: number,
      editData: any,
      stableUpdater: (value: any) => void
    ) => {
      // Only editable for project rows (not categories, not Total row)
      const isProject = cvm.isCategory === false && cvm?.Project !== 'Total';

      if (isProject) {
        // Editable input for project rows
        const onChange = (value: string) => {
          // Value is already processed by HeadcountEditableCell:
          // - "0" for empty/"-"
          // - "NA" for NA
          // - Formatted number with 1 decimal
          // - Original text for other cases
          stableUpdater(value);
        };

        const currentValue = cvm?.['Suggested Value'];

        return (
          <HeadcountEditableCell
            initialValue={currentValue}
            onChange={onChange}
            type="suggestedValue"
          />
        );
      } else {
        // Display-only for category and Total rows
        const value = cvm?.['Suggested Value'];
        
        // Format display value
        if (value === null || value === undefined || value === '') {
          return '0';
        }
        
        const strValue = String(value).trim().toLowerCase();
        if (strValue === 'na' || strValue === 'n/a') {
          return 'NA';
        }
        
        // Try to parse as number and format to 1 decimal
        const numVal = parseFloat(String(value));
        if (!isNaN(numVal)) {
          return numVal.toFixed(1);
        }
        
        // Return as-is for other text
        return String(value);
      }
    },
  });

